import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  IconButton,
  AppBar,
  Toolbar,
  Grid,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TableChart as TableChartIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';

// API base URL sabitini tanımla
const API_BASE_URL = 'https://app.hhoms.com/api';

const PublicSpreadsheetView = () => {
  const navigate = useNavigate();
  const { token } = useParams(); // Public token
  const appToken = useSelector((state) => state.appToken?.apptoken);
  const userData = useSelector((state) => state.loginData?.loginData);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [spreadsheetData, setSpreadsheetData] = useState(null);
  const [formResponses, setFormResponses] = useState([]);
  const [columns, setColumns] = useState([]);
  const [filterColumn, setFilterColumn] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    const loadPublicSpreadsheet = async () => {
      try {
        console.log('Public spreadsheet yükleniyor, token:', token);
        
        // Token direkt formId olarak kullan
        const formId = token;
        
        if (!formId) {
          setError('Geçersiz public link');
          return;
        }

        if (!appToken || !userData) {
          setError('Uygulama yetkilendirmesi gerekli');
          setLoading(false);
          return;
        }
        
        console.log('FormId:', formId);
        
        // Mevcut form API'sini kullan - headers ile
        const response = await axios.get(
          `${API_BASE_URL}/form/Libraries/${formId}`,
          {
            headers: {
              ApplicationToken: appToken,
              User: userData.id,
            },
            timeout: 30000 // Timeout'u artır
          }
        );

        console.log('Form API yanıtı:', response.data);

        if (response.data && response.data.formLibraries && response.data.formLibraries[0]) {
          const formLibrary = response.data.formLibraries[0];
          
          // formContent'ı parse et
          let formContentObj = {};
          if (formLibrary.formContent) {
            try {
              if (typeof formLibrary.formContent === 'string') {
                formContentObj = JSON.parse(formLibrary.formContent);
              } else {
                formContentObj = formLibrary.formContent;
              }
            } catch (parseError) {
              console.error('Form içeriği parse edilemedi:', parseError);
              formContentObj = {};
            }
          }

          setSpreadsheetData({
            id: formLibrary.id,
            title: formLibrary.title || formContentObj.title || 'Tablo',
            description: formContentObj.description || '',
            fields: formContentObj.fields || []
          });

          // Kolonları ayarla
          const fieldColumns = formContentObj.fields || [];
          const basicColumns = [
            { key: 'responseDate', label: 'Gönderim Tarihi', type: 'date' },
            { key: 'id', label: 'ID', type: 'number' }
          ];
          
          const allColumns = [
            ...basicColumns,
            ...fieldColumns.map(field => ({
              key: field.id,
              label: field.label || field.id,
              type: field.type || 'text'
            }))
          ];
          
          setColumns(allColumns);

          // Form responses'ları çek - headers ile
          try {
            const responsesResponse = await axios.get(
              `${API_BASE_URL}/form/Responses/list/${formId}/0/0`,
              {
                headers: {
                  ApplicationToken: appToken,
                  User: userData.id,
                },
                timeout: 30000 // Timeout'u artır
              }
            );

            console.log('Form responses yanıtı:', responsesResponse.data);

            if (responsesResponse.data && responsesResponse.data.formResponses) {
              const processedResponses = responsesResponse.data.formResponses.map(response => {
                let responseData = {};
                
                // formData'yı parse et
                if (response.formData) {
                  try {
                    if (typeof response.formData === 'string') {
                      responseData = JSON.parse(response.formData);
                    } else {
                      responseData = response.formData;
                    }
                  } catch (parseError) {
                    console.error('Form data parse edilemedi:', parseError);
                    responseData = {};
                  }
                }

                return {
                  id: response.id,
                  responseDate: response.created_at ? new Date(response.created_at).toLocaleDateString('tr-TR') : 'Bilinmiyor',
                  ...responseData
                };
              });
              
              setFormResponses(processedResponses);
              setFilteredData(processedResponses);
              console.log('İşlenmiş responses:', processedResponses);
            }
          } catch (responsesError) {
            console.error('Form responses yüklenirken hata:', responsesError);
            // Responses yüklenemese bile form yapısını gösterebiliriz
            setFormResponses([]);
            setFilteredData([]);
          }
        } else {
          setError('Tablo bulunamadı');
        }
        
      } catch (error) {
        console.error('Public spreadsheet yüklenirken hata:', error);
        if (error.response?.status === 404) {
          setError('Tablo bulunamadı');
        } else if (error.response?.status === 403) {
          setError('Bu tabloya erişim izniniz yok');
        } else {
          setError('Tablo yüklenirken hata oluştu');
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadPublicSpreadsheet();
    } else {
      setError('Geçersiz link');
      setLoading(false);
    }
  }, [token]);

  const handleFilterChange = (event) => {
    const column = event.target.value;
    setFilterColumn(column);

    if (!column) {
      setFilteredData(formResponses);
      return;
    }

    // Basit filtreleme - boş olmayan değerler
    const filtered = formResponses.filter(response => {
      const value = response[column];
      return value !== undefined && value !== null && value !== '';
    });

    setFilteredData(filtered);
  };

  const renderCellValue = (value, column) => {
    if (value === undefined || value === null || value === '') {
      return (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          -
        </Typography>
      );
    }

    // Tarih formatlaması
    if (column.type === 'date') {
      return (
        <Typography variant="body2">
          {value}
        </Typography>
      );
    }

    // Tablo türü alanlar için özel görüntüleme
    if (column.type === 'table' && typeof value === 'object' && Array.isArray(value)) {
      return (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {value.length} satır veri
          </Typography>
        </Box>
      );
    }

    // Array türü veriler
    if (Array.isArray(value)) {
      return (
        <Box>
          {value.map((item, idx) => (
            <Chip 
              key={idx} 
              label={item} 
              size="small" 
              variant="outlined" 
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </Box>
      );
    }

    // Object türü veriler
    if (typeof value === 'object') {
      return (
        <Typography variant="body2" color="text.secondary">
          {JSON.stringify(value)}
        </Typography>
      );
    }

    // Uzun metinler için kısaltma
    const stringValue = String(value);
    if (stringValue.length > 50) {
      return (
        <Typography variant="body2" title={stringValue}>
          {stringValue.substring(0, 50)}...
        </Typography>
      );
    }

    return (
      <Typography variant="body2">
        {stringValue}
      </Typography>
    );
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Tablo yükleniyor...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          onClick={handleBackToHome} 
          startIcon={<ArrowBackIcon />}
          variant="contained"
        >
          Ana Sayfaya Dön
        </Button>
      </Container>
    );
  }

  if (!spreadsheetData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Tablo verileri bulunamadı
        </Alert>
        <Button 
          onClick={handleBackToHome} 
          startIcon={<ArrowBackIcon />}
          variant="contained"
        >
          Ana Sayfaya Dön
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <AppBar position="static" sx={{ mb: 3, borderRadius: 1 }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBackToHome}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <TableChartIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {spreadsheetData.title} - Tablo Görünümü
          </Typography>
          <LockIcon sx={{ ml: 2 }} />
          <Typography variant="body2" sx={{ ml: 1 }}>
            Sadece Görüntüleme
          </Typography>
        </Toolbar>
      </AppBar>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            {/* Başlık */}
            <Typography variant="h4" gutterBottom color="primary" sx={{ textAlign: 'center' }}>
              {spreadsheetData.title}
            </Typography>
            
            {spreadsheetData.description && (
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                {spreadsheetData.description}
              </Typography>
            )}

            <Divider sx={{ mb: 3 }} />

            {/* Uyarı Mesajı */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body1">
                <strong>Bu tablo görüntüleme modunda açılmıştır.</strong>
                <br />
                Tablodaki verileri görüntüleyebilirsiniz ancak düzenleme yapamaz ve yeni veri ekleyemezsiniz.
              </Typography>
            </Alert>

            {/* Filtre */}
            <Box sx={{ mb: 3 }}>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filtrele</InputLabel>
                <Select
                  value={filterColumn}
                  onChange={handleFilterChange}
                  label="Filtrele"
                  startAdornment={<FilterListIcon sx={{ mr: 1 }} />}
                >
                  <MenuItem value="">
                    <em>Tümünü Göster</em>
                  </MenuItem>
                  {columns.map((column) => (
                    <MenuItem key={column.key} value={column.key}>
                      {column.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Toplam {filteredData.length} kayıt görüntüleniyor
              </Typography>
            </Box>

            {/* Tablo */}
            {filteredData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell 
                          key={column.key}
                          sx={{ 
                            fontWeight: 'bold',
                            bgcolor: '#f5f5f5',
                            borderRight: '1px solid #ddd',
                            minWidth: 120
                          }}
                        >
                          {column.label}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            ({column.type})
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.map((row, rowIndex) => (
                      <TableRow 
                        key={rowIndex}
                        sx={{ 
                          '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                          '&:hover': { bgcolor: '#f0f0f0' }
                        }}
                      >
                        {columns.map((column) => (
                          <TableCell 
                            key={column.key}
                            sx={{ 
                              borderRight: '1px solid #ddd',
                              maxWidth: 200,
                              overflow: 'hidden'
                            }}
                          >
                            {renderCellValue(row[column.key], column)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                Bu tabloda henüz veri bulunmuyor.
              </Alert>
            )}

            {/* Alt Bilgi */}
            <Divider sx={{ my: 3 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Bu tabloya veri eklemek için lütfen tablo sahibi ile iletişime geçin.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PublicSpreadsheetView;
